import { useMemo, useState } from "react";
import { api, getUnitLabel } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function BookingModal({ listing, onClose }) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [quantity, setQuantity] = useState(1);
  const [method, setMethod] = useState(listing.pickup_available ? "pickup" : "delivery");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const remaining = listing.quantity_remaining !== undefined ? listing.quantity_remaining : listing.quantity;

  // Quick-pick chips (25%, 50%, 100%) — recomputed only when `remaining` changes,
  // not on every keystroke.
  const quickChips = useMemo(() => {
    const candidates = [
      Math.max(1, Math.floor(remaining * 0.25)),
      Math.max(1, Math.floor(remaining * 0.5)),
      Math.max(1, Math.floor(remaining)),
    ];
    return candidates.filter((v, i, a) => a.indexOf(v) === i);
  }, [remaining]);
  const submit = async (e) => {
    e.preventDefault();
    if (quantity <= 0 || quantity > remaining) {
      toast.error(`${t("book.quantityErr")} ${remaining}`);
      return;
    }
    if (method === "delivery" && !address.trim()) {
      toast.error(t("book.addressErr"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        listing_id: listing.id,
        quantity: Number(quantity),
        method,
        address: method === "delivery" ? address : null,
        note,
      });
      toast.success(t("book.sent"));
      onClose();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("book.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--bone)] border border-[var(--ink)] hard-shadow max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
        <div className="flex items-center justify-between p-5 border-b border-[var(--ink-200)]">
          <div>
            <div className="label-tech">{t("book.label")}</div>
            <h2 className="font-display text-xl mt-1">{listing.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--surface-alt)]" data-testid="booking-close-btn"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5">
          <div>
            <div className="label-tech mb-2">
              {t("book.quantity")} ({getUnitLabel(listing.unit, lang)}) · {t("book.remaining")} {remaining} / {listing.quantity}
            </div>
            <input type="number" min="1" max={remaining} step="0.1" className="input-tech" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="booking-quantity-input" />
            <div className="mt-2 flex flex-wrap gap-2">
              {quickChips.map((v) => (
                <button key={v} type="button" onClick={() => setQuantity(v)} className="px-3 py-1.5 text-xs font-mono-tech uppercase tracking-[0.1em] border border-[var(--ink-200)] hover:bg-[var(--surface-alt)]" data-testid={`booking-qty-chip-${v}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="label-tech mb-2">{t("book.method")}</div>
            <div className="grid grid-cols-2 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
              {listing.pickup_available && (
                <button type="button" onClick={() => setMethod("pickup")} className={`p-3 text-sm ${method === "pickup" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid="booking-method-pickup">{t("detail.pickup")}</button>
              )}
              {listing.delivery_available && (
                <button type="button" onClick={() => setMethod("delivery")} className={`p-3 text-sm ${method === "delivery" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`} data-testid="booking-method-delivery">{t("detail.delivery")}</button>
              )}
            </div>
          </div>

          {method === "delivery" && (
            <div>
              <div className="label-tech mb-2">{t("book.address")}</div>
              <input type="text" className="input-tech" placeholder={t("book.addressPh")} value={address} onChange={(e) => setAddress(e.target.value)} data-testid="booking-address-input" />
            </div>
          )}

          <div>
            <div className="label-tech mb-2">{t("book.note")}</div>
            <textarea rows={3} className="input-tech" placeholder={t("book.notePh")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="booking-note-input" />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center py-4" data-testid="booking-submit-btn">
            {submitting ? t("book.sending") : t("book.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
